import { createFeatureSelector, createSelector } from '@ngrx/store';
import { StudioFeature } from './studio.actions';
import { StudioState, VideoControlData } from './studio.reducer';
import { StudioScene, StudioVideoShapeDescriptor } from './studio.types';
import { StudioTextShapeDescriptor } from './text/studio-text-shape';
import { selectUserFeature } from '../../../user/user.selector';
import {
  _screenX,
  _screenY,
  portraitScreenX,
  portraitScreenY,
} from '../../../services/canvas/canvas.service';

// Feature selector
export const selectStudioFeature = createFeatureSelector<{
  studio: StudioState;
}>(StudioFeature);

// Select the studio state // -- // -- //
export const selectStudioState = createSelector(
  selectStudioFeature,
  state => state.studio,
);

// --------------- APP STATE ------------------ //
export const selectStudioAppState = createSelector(
  selectStudioState,
  state => state.state,
);

// --------------- CRUD STATE ------------------ //
export const isStudioIndexLoading = createSelector(
  selectStudioState,
  state => state.crudState.index == 'loading',
);

export const isStudioIndexError = createSelector(
  selectStudioState,
  state => state.crudState.index == 'error',
);

export const isStudioIndexIdle = createSelector(
  selectStudioState,
  state => state.crudState.index == 'idle',
);

export const selectCurrentStudioScene = createSelector(
  selectStudioState,
  state => state.scenes[state.currentSceneId],
);

// --------------- PROJECT ------------------ //
export const selectCurrentProject = createSelector(
  selectStudioState,
  state => state.projects[state.currentProjectID],
);

export const selectProjectsByEditTime = createSelector(
  selectStudioState,
  selectUserFeature,
  ({ projects }, { myId }) => {
    // console.log('---- myId ----', myId);
    return Object.values(projects).sort((a, b) =>
      new Date(a.edits?.[myId]?.time || '1970-01-01').getTime() <
      new Date(b.edits?.[myId]?.time || '1970-01-01').getTime()
        ? 1
        : -1,
    );
  },
);

export const selectDeleteProjectState = createSelector(
  selectStudioState,
  state => {
    return state.deleteProjectState;
  },
);

export const selectCurrentProjectLabel = createSelector(
  selectStudioState,
  state => state.projects[state.currentProjectID]?.name,
);

export const selectSelectedShape = createSelector(selectStudioState, state => {
  // -- //
  const { selectedShapes } = state;
  // -- //
  if (Object.keys(selectedShapes || {}).length == 0) {
    return;
  }
  const [shapeId] = Object.keys(selectedShapes);
  const { shapes } = state.scenes[state.currentSceneId];
  return {
    id: shapeId,
    descriptor: shapes[shapeId] as StudioTextShapeDescriptor,
  };
});
export const selectStudioScenes = createSelector(selectStudioState, state => {
  const array = Object.values(state.scenes).sort((a, b) => a.index - b.index);
  if (array.length == 0) {
    return array;
  }
  return [
    ...array,
    {
      id: 'add-after-last',
      index: array.length,
      positionIndex: array.length,
    },
  ];
});

export const selectCurrentScene = createSelector(selectStudioState, state => {
  const { currentSceneId, scenes } = state;

  return scenes[currentSceneId];
});

export const selectPanelStable = createSelector(
  selectStudioState,
  state => state.panelStable,
);
export const selectCurrentStudioSceneId = createSelector(
  selectStudioState,
  state => state.currentSceneId,
);

export const selectStudioImages = createSelector(
  selectStudioState,
  state => state.images || {},
);

export const selectStudioVideos = createSelector(
  selectStudioState,
  state => state.videos || {},
);

export const selectStudioVideoInstances = createSelector(
  selectStudioState,
  state => {
    const videoInstances = {};
    Object.values(state.videos || {}).forEach(video => {
      Object.values(video.instances || {}).forEach(instance => {
        videoInstances[instance.id] = instance;
      });
    });
    return videoInstances;
  },
);
export const selectSelectedMedia = createSelector(
  selectStudioState,
  state => state.selectedMedia,
);

// Individual selectors
export const selectStudioError = createSelector(
  selectStudioState,
  state => state.error,
);

export const selectStudioMode = createSelector(
  selectStudioState,
  state => state.mode,
);

export const selectStudioData = createSelector(
  selectStudioState,
  state => state.data,
);

// Derived selectors

// CRUD selectors
export const selectStudioCrudEvents = createSelector(
  selectStudioState,
  state => state.crudEvents,
);

export const selectStudioHasChanges = createSelector(
  selectStudioCrudEvents,
  crudEvents =>
    Object.keys(crudEvents.posts).length > 0 ||
    Object.keys(crudEvents.patches).length > 0 ||
    Object.keys(crudEvents.deletes).length > 0,
);

export const selectChangeTookPlace = createSelector(
  selectStudioState,
  state => state.changeTookPlace,
);

export const selectCurrentStudioProjectID = createSelector(
  selectStudioState,
  state => state.currentProjectID,
);

export const selectCurrentStudioSceneIndex = createSelector(
  selectStudioState,
  state => state.scenes[state.currentSceneId]?.positionIndex,
);

export const selectCurrentStudioProject = createSelector(
  selectStudioState,
  state => state.projects[state.currentProjectID],
);

// Add scene button positions
export interface AddSceneButtonPosition {
  index: number;
  afterSceneId?: string;
}

export const selectAddSceneButtonPositions = createSelector(
  selectStudioScenes,
  scenes => {
    const positions: AddSceneButtonPosition[] = [];

    // Add button at the very beginning (before first scene)
    positions.push({ index: 0, afterSceneId: undefined });

    // Add button after each scene
    scenes.forEach((scene, idx) => {
      positions.push({
        index: idx + 1,
        afterSceneId: scene.id,
      });
    });

    return positions;
  },
);

// Current scene overlay data (for delete/add buttons)
export interface CurrentSceneOverlay {
  index: number;
  sceneId: string;
  totalScenes: number;
}

export const selectIsPortraitMode = createSelector(
  selectStudioState,
  state => state.isPortraitMode,
);

export const selectCurrentSceneOverlay = createSelector(
  selectStudioScenes,
  selectCurrentStudioSceneId,
  (scenes, currentSceneId): CurrentSceneOverlay | null => {
    if (!currentSceneId || scenes.length === 0) {
      return null;
    }
    const index = scenes.findIndex(s => s.id === currentSceneId);
    if (index === -1) {
      return null;
    }
    return {
      index,
      sceneId: currentSceneId,
      totalScenes: scenes.length,
    };
  },
);

// Video controls data
export interface VideoControlsSection {
  id: string;
  duration: number;
}

export interface VideoControlsData {
  scenes: StudioScene[];
  currentSceneId: string | undefined;
  currentSceneIndex: number;
  totalScenes: number;
  sections: VideoControlsSection[];
  totalDuration: number;
  gap: number;
}

export const selectVideoControls = createSelector(
  selectStudioState,
  (state): VideoControlData => {
    const { scenes, sceneDurations, currentSceneId, videos, images } = state;

    const scenesArray = Object.values(scenes)
      .sort((a, b) => a.positionIndex - b.positionIndex)
      .filter(s => s.id !== 'add-after-last');

    // Remove the 'add-after-last' scene

    const sum = sceneDurations?.reduce((sum, current) => sum + current, 0) || 0;

    const durations = state.sceneDurations || [];

    let offset = 0;
    // const gap = 3;
    // gap is ten percent of the total duration
    // const sumGap = 0.1 * sum;
    // const gap = sumGap / (scenesArray.length - 1);
    const startsBySceneId: Record<string, number> = {};
    const sceneSections = scenesArray.map((scene, index) => {
      // Get the media for this scene (video or image)
      // Check if scene has videos or images arrays and get the first one
      const videoId = scene.videoInstances?.[0];
      const imageId = scene.images?.[0];
      const mediaId = videoId || imageId;
      const selectedMedia = mediaId
        ? videos?.[mediaId] || images?.[mediaId]
        : undefined;

      const object = {
        sceneId: scene.id,
        start: offset,
        end: offset + durations[index],
        duration: durations[index],
        index,
        selectedMedia,
      };
      startsBySceneId[scene.id] = offset;
      offset += durations[index];
      return object;
    });
    return {
      currentTokenPosition: startsBySceneId[currentSceneId] || 0,
      totalDuration: sum,
      sceneSections,
      startsBySceneId,
    };
  },
);

export const selectIsStudioMode = createSelector(
  selectStudioState,
  state => !!state.currentSceneId && Object.keys(state.scenes).length > 0,
);

// Root pixel dimensions for the current scene, in both orientations
export interface RootDimensions {
  landscape: { w: number; h: number };
  portrait: { w: number; h: number };
}

export const selectRootDimensions = createSelector(
  selectCurrentScene,
  (scene): RootDimensions => {
    const padding = scene?.padding ?? 0.05;
    return {
      landscape: {
        w: (1 - padding * 2) * _screenX,
        h: _screenY - 2 * padding * _screenX,
      },
      portrait: {
        w: (1 - padding * 2) * portraitScreenX,
        h: portraitScreenY - 2 * padding * portraitScreenX,
      },
    };
  },
);

// Clips in order selector - returns video clips from all scenes in order
export const selectClipsInOrder = createSelector(
  selectStudioState,
  (state): StudioVideoShapeDescriptor[] => {
    const { scenes } = state;

    // Sort scenes by positionIndex
    const scenesArray = Object.values(scenes)
      .filter(s => s.id !== 'add-after-last')
      .sort((a, b) => a.positionIndex - b.positionIndex);

    const videoShapes: StudioVideoShapeDescriptor[] = [];

    for (const scene of scenesArray) {
      const { shapes } = scene;
      if (!shapes) continue;

      // Find video shapes in this scene
      for (const shape of Object.values(shapes)) {
        // Check if this is a video shape (has videoId or videoInstanceId)
        const videoShape = shape as StudioVideoShapeDescriptor;
        if (videoShape.videoId || videoShape.videoInstanceId) {
          videoShapes.push(videoShape);
        }
      }
    }

    return videoShapes;
  },
);
